import { useEffect, useRef, useState } from "react";
import { Button } from "../ui/Button";

/**
 * CRÉER UNE DONNÉE ÉTENDUE — la question, pas seulement sa place dans un formulaire.
 *
 * ⚠️ UNE MODALE, ET NON UNE CRÉATION AU FIL DU CLIC. C'est la seule écriture de cet
 * éditeur qui ne passe PAS par le brouillon : une question doit exister avant qu'on
 * puisse la poser, donc elle part immédiatement et ne s'annule pas en abandonnant le
 * formulaire. Une action qui échappe au « Abandonner » du reste doit être demandée
 * explicitement — sinon on en sème sans le savoir, et il en reste des orphelines.
 *
 * ⚠️ LA PORTÉE EST ANNONCÉE, PAS CHOISIE. Elle est figée sur « cet événement » — voir
 * `creerQuestion`. L'écrire dans la modale évite la question qui suivrait sinon :
 * « est-ce que ça va apparaître dans mes autres salons ? ».
 *
 * Displayed strings are French: the organiser is a French speaker.
 */

/** Les types qui appellent une liste de choix. Les autres n'en ont pas à saisir. */
const WITH_CHOICES = new Set(["select", "radio", "checkbox"]);

const TYPES: Array<{ value: string; label: string }> = [
  { value: "input", label: "Texte" },
  { value: "input-number", label: "Nombre" },
  { value: "textarea", label: "Plusieurs lignes" },
  { value: "select", label: "Liste (un choix)" },
  { value: "radio", label: "Boutons (un choix)" },
  { value: "checkbox", label: "Cases (plusieurs)" },
  { value: "file", label: "Pièce jointe" },
];

export function NewQuestion({
  open,
  busy,
  error,
  onCancel,
  onCreate,
}: {
  open: boolean;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onCreate: (name: string, type: string, options: string[]) => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [name, setName] = useState("");
  const [type, setType] = useState("input");
  const [choice, setChoices] = useState<string[]>(["", ""]);

  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (open && !el.open) {
      // Repartir vierge : la modale précédente a pu être annulée à moitié remplie.
      setName("");
      setType("input");
      setChoices(["", ""]);
      el.showModal();
    }
    if (!open && el.open) el.close();
  }, [open]);

  const hasChoices = WITH_CHOICES.has(type);
  const selected = choice.map((c) => c.trim()).filter(Boolean);
  /* Une question à choix sans choix ne se répond pas ; un nom vide ne se relit pas. */
  const full = name.trim() !== "" && (!hasChoices || selected.length > 0);

  return (
    <dialog
      ref={dialog}
      className="ask edit nvd"
      onCancel={(e) => {
        e.preventDefault();
        if (!busy) onCancel();
      }}
      onClick={(e) => {
        if (e.target === dialog.current && !busy) onCancel();
      }}
    >
      <div className="ask__b">
        <div className="edit__h">
          <b>Créer une donnée</b>
        </div>

        <p className="nvd__p">
          Elle appartiendra à <b>cet événement seul</b> : les autres événements du
          domaine ne la verront pas, et elle ne suivra pas le participant d'un salon à
          l'autre. Pour une donnée permanente ou partagée entre plusieurs événements,
          passez par le back office.
        </p>

        <label className="nvd__l">
          Question posée
          <input
            type="text"
            value={name}
            autoFocus
            placeholder="Participerez-vous au cocktail ?"
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label className="nvd__l">
          Type de champ
          <select value={type} onChange={(e) => setType(e.target.value)}>
            {TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>

        {hasChoices && (
          <div className="nvd__l">
            <span>Réponses possibles</span>
            {/* ⚠️ SEUL LE LIBELLÉ SE SAISIT. Chaque réponse porte aussi une clé, sous
                laquelle elle est enregistrée — elle est tirée au sort, comme le fait le
                back office. La demander ne servirait qu'à donner l'occasion de la
                saisir deux fois pareil. */}
            {choice.map((c, i) => (
              <div className="nvd__o" key={i}>
                <input
                  type="text"
                  value={c}
                  placeholder={`Réponse ${i + 1}`}
                  aria-label={`Réponse ${i + 1}`}
                  onChange={(e) =>
                    setChoices(choice.map((x, j) => (j === i ? e.target.value : x)))
                  }
                />
                <button
                  type="button"
                  className="fed__c"
                  aria-label={`Retirer la réponse ${i + 1}`}
                  disabled={choice.length <= 1}
                  onClick={() => setChoices(choice.filter((_, j) => j !== i))}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              className="fed__c nvd__add"
              onClick={() => setChoices([...choice, ""])}
            >
              + Ajouter une réponse
            </button>
          </div>
        )}

        {error && (
          <p className="fed__err" role="alert">
            {error}
          </p>
        )}

        <div className="ask__a">
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            onClick={onCancel}
          >
            Annuler
          </Button>
          <Button
            type="button"
            loading={busy}
            disabled={!full}
            /* ⚠️ PAS DE CHOIX POUR UN TYPE QUI N'EN A PAS. Saisis puis abandonnés en
               repassant en « Texte », ils partaient quand même : la donnée naissait à
               choix, posée en texte libre (#287). */
            onClick={() => onCreate(name.trim(), type, hasChoices ? selected : [])}
          >
            Créer
          </Button>
        </div>
      </div>
    </dialog>
  );
}
